import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { trn } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { translations } from '@adornis/buchungsheld-db/translations/translation.js';
import { trButton } from '@adornis/buchungsheld-theme/components/tr-button-small.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { mailTemplateDefinitions } from '@adornis/mails/db/mail-template-definition.js';
import { assembleMailTemplate, MailTemplate } from '@adornis/mails/db/mail-template.js';
import { html, nothing } from 'lit';
import { map, Observable, of } from 'rxjs';
import { batchCustomMailRequests, sendPendingMailBatches } from './custom-mail-batches.js';

@Entity()
export class CustomMailRequest extends AdornisEntity {
  static override _class = 'CustomMailRequest';

  @Field(type => [String])
  mailAddresses!: string[];

  @Field(type => [String])
  cc!: string[];

  @Field(type => [String])
  bcc!: string[];

  @Field(type => String)
  subject!: string;

  @Field(() => String)
  content!: string;
}

const mailAddressDisplay = stateful(
  (p: {
    mailAddresses: string[];
    cc?: string[] | null;
    bcc?: string[] | null;
    onSelectAddress?: (address: string, index: number) => void;
    selectedAddressIndex?: number;
  }) => {
    const [showAllAddresses, setShowAllAddresses] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(p.selectedAddressIndex ?? 0);

    const handleAddressClick = (address: string, index: number) => {
      setSelectedIndex(index);
      if (p.onSelectAddress) {
        p.onSelectAddress(address, index);
      }
    };

    return html`
      <x-flex space="sm">
        <x-text bold> Mail versenden an folgende Mail-Adressen (${p.mailAddresses?.length ?? 0}): </x-text>
        <x-flex horizontal wrap space="sm">
          ${(p.mailAddresses ?? []).slice(0, !showAllAddresses ? 3 : undefined).map(
            (address, index) => html`
              <x-button
                mode="text"
                @click=${() => handleAddressClick(address, index)}
                ${css({
                  textDecoration: 'underline',
                  color: 'primary',
                  cursor: 'pointer',
                })}
              >
                <x-text ${css({ fontWeight: selectedIndex === index ? 'bold' : 'normal' })}>${address}</x-text>
              </x-button>
            `,
          )}
          ${!showAllAddresses && (p.mailAddresses?.length ?? 0) > 3
            ? html`
                <x-button mode="text" @click=${() => setShowAllAddresses(true)}>
                  ... ${p.mailAddresses!.length - 3} weitere Mail-Adressen anzeigen
                </x-button>
              `
            : nothing}
        </x-flex>

        <!-- mail copy -->
        ${(p.bcc?.length ?? 0 > 0)
          ? html`
              <x-hr></x-hr>
              <x-text bold> Kopie versenden an folgende Mail-Adressen (${p.bcc?.length ?? 0}): </x-text>
              <x-text ${css({ textDecoration: 'underline', color: 'blue' })}> ${p.bcc?.join(', ')} </x-text>
            `
          : nothing}
      </x-flex>
    `;
  },
);

const chooseMailTemplate = stateful(
  (p: {
    templates: MailTemplate[] | null; //
    choose: (template: string | null) => void;
  }) => {
    if (!p.templates) return html`<tr-loader></tr-loader>`;

    const buttons = p.templates.map(t => trButton({ text: t.name, action: () => p.choose(t.emailType as string) }));

    return html`
      <x-flex vertical space-between end space="sm" ${css({ maxHeight: '80vh' })}>
        <mail-template-select></mail-template-select>
        <x-flex space="sm" ${css({ overflowY: 'auto', minHeight: '0' })}> ${buttons} </x-flex>
        <x-button mode="outline" @click=${() => p.choose(null)}> ${trn(translations.LOGIN_PAGE.back as any)} </x-button>
      </x-flex>
    `;
  },
);

type CustomMailRequestEditorProps = {
  availableMailTemplates: MailTemplate[];
  mails: {
    mailAddresses: string[];
    cc?: string[] | null;
    bcc?: string[] | null;
    contextVariables: Record<string /* string */, string>;
  }[];
  sendMails: (mails: CustomMailRequest[]) => Promise<void> | Observable<string>;
};

export const customMailRequestEditor = stateful((options: CustomMailRequestEditorProps & { resolve: () => void }) => {
  const [customTemplateVariables, setCustomTemplateVariables] = useState({} as Record<string, string>);
  const [chooseFromTemplates, setChooseFromTemplates] = useState(false);
  const [string, setstring] = useState<string>(options.availableMailTemplates.at(0)?.emailType as string);
  const [selectedMailIndex, setSelectedMailIndex] = useState(0);
  // const [currentMailContent, setCurrentMailContent] = useState<string | undefined>(undefined);
  const mailTemplate = options.availableMailTemplates.find(t => t.emailType === string);
  const mailTemplateContent = useObservable(
    () => mailTemplate?.whenChanging.pipe(map(t => t.content)) ?? of(void 0),
    [mailTemplate?._id],
  );
  const [loading, setLoading] = useState(false);
  const [delivery] = useState({ pending: null as CustomMailRequest[][] | null, sending: false });
  const sentCount =
    options.mails.length -
    (delivery.pending?.reduce((count, batch) => count + batch.length, 0) ?? options.mails.length);

  const p = options.mails.at(selectedMailIndex) || options.mails.at(0)!;

  const handleSelectAddress = (address: string, mailIndex: number) => {
    setSelectedMailIndex(mailIndex);
  };

  const mergedTemplateVariables = {
    ...p.contextVariables,
    ...customTemplateVariables,
  };

  if (!string) {
    console.warn('no mail template set');
    return nothing;
  }

  if (!mailTemplate) {
    console.error('mail template not found');
    return nothing;
  }

  if (chooseFromTemplates)
    return chooseMailTemplate({
      templates: options.availableMailTemplates,
      choose: template => {
        if (template) setstring(template);
        setChooseFromTemplates(false);
      },
    });

  // available / not available mail variables
  const definition = mailTemplateDefinitions[string];
  const variables = definition?.variables as string[];
  const notAvailableMailTemplateVariables =
    (variables.filter(v => {
      const contextValue = p.contextVariables?.[v];
      const contextValueIsMissing = contextValue === undefined || contextValue === null;
      return contextValueIsMissing;
    }) as string[]) ?? [];

  // check if all used variables are set
  const usedVariables = [] as string[];
  if (mailTemplateContent)
    for (const variable of definition?.variables ?? []) {
      const reg = new RegExp(`\\{\\{${variable}\\}\\}`, 'g');
      if (mailTemplateContent.match(reg) !== null || mailTemplate.subject?.match(reg) !== null) {
        usedVariables.push(variable as string);
      }
    }
  const missingVariables = notAvailableMailTemplateVariables.filter(variableThatCantBeFilledInAutomatically => {
    const isUsed = usedVariables.includes(variableThatCantBeFilledInAutomatically);
    const isManuallyFilledOut = variableThatCantBeFilledInAutomatically in mergedTemplateVariables;
    if (!isUsed) return false;
    if (isManuallyFilledOut) return false;
    return true;
  });
  const allVariablesAreSet = missingVariables.length === 0;

  async function sendMails() {
    if (delivery.sending) return;
    delivery.sending = true;
    setLoading(true);
    try {
      if (!delivery.pending) {
        if (!mailTemplate) throw new Error('mail template is undefined');
        const mails = [] as CustomMailRequest[];
        for (const p of options.mails) {
          const infos = { ...p.contextVariables, ...customTemplateVariables };
          const mail = assembleMailTemplate({ template: mailTemplate, infos });
          if (!mail) throw new Error('mail is undefined');

          const getRenderString = (data): string => {
            if (data == null) return '';
            if (typeof data === 'string') return data;
            if (typeof data === 'symbol') return '';
            if (Array.isArray(data)) return data.map(e => getRenderString(e)).join('');
            const { strings, values } = data;
            if (!values || typeof values[Symbol.iterator] !== 'function') return String(data);
            const v = [...values, ''].map(e => getRenderString(e));
            if (!strings) return v.join('');
            return strings.reduce((acc, s, i) => acc + s + v[i], '');
          };
          const content = (() => {
            if (typeof mail.content === 'string') return mail.content;
            return getRenderString(mail.content);
          })();

          mails.push(
            new CustomMailRequest({
              subject: mail.subject,
              content: content,
              mailAddresses: p.mailAddresses,
              cc: p.cc,
              bcc: p.bcc,
            }),
          );
        }
        delivery.pending = batchCustomMailRequests(mails);
      }
      await sendPendingMailBatches(delivery.pending, async batch => {
        const sentBefore = options.mails.length - delivery.pending!.reduce((count, b) => count + b.length, 0);
        const handle = options.sendMails(batch);
        // The server reports progress per batch; show it relative to all mails instead.
        if (handle instanceof Observable)
          await XNativeDialog.waitFor(
            handle.pipe(map((_, i) => `${sentBefore + i + 1}/${options.mails.length} E-Mails versandt`)),
          );
        else await XNativeDialog.waitFor(handle);
      });
      options.resolve();
      void XSnackbar.show('Es wurden ' + options.mails.length + ' E-mails versandt.');
    } catch (err) {
      console.error('sendMails failed:', err);
      const completed =
        options.mails.length -
        (delivery.pending?.reduce((count, batch) => count + batch.length, 0) ?? options.mails.length);
      void XNativeDialog.alert(
        `${completed} von ${options.mails.length} E-Mails erfolgreich abgeschlossen. Versand fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}${delivery.pending ? ' Erneut senden wiederholt nur die offenen Pakete mit den bereits vorbereiteten Mails. Im fehlgeschlagenen Paket können bereits E-Mails versandt worden sein.' : ''}`,
      );
      delivery.sending = false;
      setLoading(false);
    }
  }

  function close(val) {
    options.resolve();
  }

  const actions = html`
    <x-flex horizontal space-between end space="sm">
      ${[
        trButton({ text: 'Abbrechen', action: () => close(false), mode: 'outline', notClickable: loading }),
        trButton({
          icon: 'send',
          text: delivery.pending ? 'Verbleibende Mails senden' : 'Mail senden',
          action: () => sendMails(),
          notClickable: (!delivery.pending && !allVariablesAreSet) || loading,
          tooltip: delivery.pending || allVariablesAreSet ? undefined : 'Es sind nicht alle Variablen gesetzt',
        }),
      ]}
    </x-flex>
  `;

  const variableForm =
    notAvailableMailTemplateVariables.length > 0
      ? html`
          <tr-h3> ${mailTemplate.name} </tr-h3>
          ${notAvailableMailTemplateVariables.map(
            variable =>
              html`<x-input
                placeholder=${variable}
                @blur=${e => {
                  let value = e.target.value.value;
                  if (value === '') value = null;
                  setCustomTemplateVariables({ ...customTemplateVariables, [variable]: value });
                }}
              ></x-input>`,
          )}
        `
      : nothing;

  const editor = html`
    <tr-mail-editor send-only .value=${mailTemplate} .contextVariables=${mergedTemplateVariables}></tr-mail-editor>
  `;

  return html`
    <x-flex space="lg" ${css({ maxHeight: '80vh', overflowY: 'auto', maxWidth: '90vw' })}>
      ${[
        mailAddressDisplay({
          ...p,
          mailAddresses: options.mails.map(m => m.mailAddresses.at(0) || ''),
          selectedAddressIndex: selectedMailIndex,
          onSelectAddress: handleSelectAddress,
        }),
        delivery.pending
          ? html`<x-text>
              ${sentCount} von ${options.mails.length} E-Mails erfolgreich abgeschlossen. Erneut senden setzt den
              Versand mit den bereits vorbereiteten Mails fort. Abgeschlossene Pakete werden übersprungen.
            </x-text>`
          : html`
              ${trButton({ tr: ['mail_template', 'use_template'], action: () => setChooseFromTemplates(true) })}
              ${variableForm} ${editor}
            `,
        actions,
      ]}
    </x-flex>
  `;
});

export const customMailRequestDialog = (p: CustomMailRequestEditorProps) => {
  const content = resolve => html` <x-card>${customMailRequestEditor({ ...p, resolve })}</x-card> `;

  return XNativeDialog.showPopup({
    content,
  });
};
